import { ArrowTopRightOnSquareIcon } from '@heroicons/react/16/solid';
import {
  AlertCircle,
  Calendar,
  CalendarClock,
  Check,
  CheckCircle,
  ChevronDown,
  CreditCard,
  Tag,
  User,
} from 'lucide-react';
import * as React from 'react';

import { CmsPushBadge } from '@/components/push-to-cms/CmsPushBadge';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { Task } from '@/services/types/client-intake-types';
import {
  CoverageCategory,
  CoverageStub,
} from '@/services/types/coverage-types';
import type { ExternalNoteActionSchema } from '@/services/types/external-note-types';
import { TaskExternalCMSUpdateStatus } from '@/services/types/task-types';
import { User as UserType } from '@/services/types/user-types';
import {
  formatTaskDate,
  getTaskPriority,
  isTaskPastDue,
} from '@/utils/taskDateUtils';

import { TaskDatePicker } from './TaskDatePicker';

interface TaskMetadataProps {
  task: Task;
  staffUsers: UserType[];
  isStaffUsersLoading: boolean;
  onAssigneeChange: (userId: string | null) => void;
  onDueDateChange: (date: Date | null) => void;
  onCompletedDateChange: (date: Date | null) => void;
  onOrderChange?: (order: number | null) => void;
  onCMSUpdateStatusChange?: (status: TaskExternalCMSUpdateStatus) => void;
  coverages?: CoverageStub[];
  isCoveragesLoading?: boolean;
  isCoverageUpdating?: boolean;
  onCoverageChange?: (coverageId: string | null) => void;
  managingParalegal?: {
    id: string;
    first_name: string;
    last_name: string;
  } | null;
  caseManager?: {
    id: string;
    display_name: string;
  } | null;
  pushAction?: ExternalNoteActionSchema;
}

/** Compact metadata chip used in the secondary "More details" row */
const MetadataItem: React.FC<{
  label: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}> = ({ label, icon, children, className }) => (
  <div className={cn('flex items-center gap-1.5 min-w-0', className)}>
    {icon && <div className="text-slate-500 flex-shrink-0">{icon}</div>}
    <span className="text-[11px] font-semibold text-slate-500 shrink-0">{label}:</span>
    <div className="text-[11px] font-semibold text-slate-800 truncate">{children}</div>
  </div>
);

/** Helper to render source content without nested ternaries */
const renderSourceContent = (task: Task) => {
  if (task.source === 'finch_user' && task.created_by_name) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="cursor-help">
            {task.source_display || task.source}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-xs">Created by: {task.created_by_name}</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  if (task.source === 'finch_custom_task') {
    return (
      <div className="flex items-center gap-2">
        <span>{task.source_display || task.source}</span>
        {task.firm && (
          <Tooltip>
            <TooltipTrigger asChild>
              <a
                href={`/firms/${task.firm}/services`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-blue-600 hover:text-blue-700 hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                <ArrowTopRightOnSquareIcon className="h-3 w-3" />
              </a>
            </TooltipTrigger>
            <TooltipContent>
              <p className="text-xs">View Firm Task Template</p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    );
  }

  return <span>{task.source_display || task.source || '—'}</span>;
};

/** Linear-style priority bars icon (like Linear app) */
const PriorityIcon: React.FC<{ level: string }> = ({ level }) => {
  const barCount = level === 'immediate' ? 4 : level === 'urgent' ? 3 : 2;
  const activeColor =
    level === 'immediate' ? '#ef4444'
    : level === 'urgent' ? '#f59e0b'
    : '#94a3b8';
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" className="shrink-0 align-middle" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
      {[0, 1, 2, 3].map((i) => (
        <rect
          key={i}
          x={1 + i * 4}
          y={14 - (i + 1) * 3}
          width="3"
          rx="0.5"
          height={(i + 1) * 3}
          fill={i < barCount ? activeColor : '#e2e8f0'}
        />
      ))}
    </svg>
  );
};

export const TaskMetadata: React.FC<TaskMetadataProps> = ({
  task,
  staffUsers,
  isStaffUsersLoading,
  onAssigneeChange,
  onDueDateChange,
  onCompletedDateChange,
  onOrderChange,
  onCMSUpdateStatusChange,
  coverages = [],
  isCoveragesLoading = false,
  isCoverageUpdating = false,
  onCoverageChange,
  managingParalegal,
  caseManager,
  pushAction,
}) => {
  const serverPriority = getTaskPriority(task);
  const isOverdue = isTaskPastDue(task);
  const [assigneeSelectOpen, setAssigneeSelectOpen] = React.useState(false);
  const [isUpdatingCMS, setIsUpdatingCMS] = React.useState(false);
  const [optimisticPriority, setOptimisticPriority] = React.useState<string | null>(null);
  const [datePickerOpen, setDatePickerOpen] = React.useState(false);

  // Sync optimistic state back when server catches up
  React.useEffect(() => {
    setOptimisticPriority(null);
  }, [serverPriority]);

  const priority = optimisticPriority ?? serverPriority;

  const handlePriorityClick = () => {
    const next =
      priority === 'immediate' ? 'normal'
        : priority === 'urgent' ? 'immediate'
        : 'urgent';
    setOptimisticPriority(next);

    const orderMap: Record<string, number> = { normal: 0, urgent: -1, immediate: -100 };
    onOrderChange?.(orderMap[next]);
  };

  const handleCMSUpdateClick = async () => {
    if (!onCMSUpdateStatusChange) return;

    setIsUpdatingCMS(true);
    try {
      await onCMSUpdateStatusChange(TaskExternalCMSUpdateStatus.UPDATED);
    } finally {
      setIsUpdatingCMS(false);
    }
  };

  const selectedCoverageId =
    (task.coverage?.id as string | undefined) ||
    (task.coverage_id as string | undefined) ||
    null;

  const coverageOptions = React.useMemo(() => {
    const optionMap = new Map<string, CoverageStub>();
    coverages.forEach((coverage) => optionMap.set(coverage.id, coverage));
    if (selectedCoverageId && task.coverage) {
      optionMap.set(selectedCoverageId, task.coverage);
    }
    return Array.from(optionMap.values());
  }, [coverages, selectedCoverageId, task.coverage]);

  const formatCoverageLabel = (coverage: CoverageStub) => {
    const propertyLabel = coverage.named_insured?.find((value) => value?.trim())?.trim();
    if (coverage.category === CoverageCategory.PROPERTY) {
      return propertyLabel || 'Property Coverage';
    }

    const carrierName = coverage.carrier?.name?.trim() || 'Unknown Carrier';
    const category =
      coverage.category
        ?.replace(/_/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase()) || 'Coverage';
    const policyIdentifier =
      coverage.policy_identifiers && coverage.policy_identifiers.length > 0
        ? coverage.policy_identifiers[0].identifier_value
        : null;

    return [carrierName, category, policyIdentifier]
      .filter(Boolean)
      .join(' • ');
  };

  const handleCoverageSelect = (value: string) => {
    if (!onCoverageChange) return;
    if (value === 'unlinked') {
      onCoverageChange(null);
      return;
    }
    onCoverageChange(value);
  };

  const [showMoreDetails, setShowMoreDetails] = React.useState(false);

  const priorityLabel =
    priority === 'immediate' ? 'Immediate'
    : priority === 'urgent' ? 'Urgent'
    : 'Normal';


  const chipBase = 'inline-flex items-center gap-1 h-6 px-1.5 rounded-md border border-slate-200 text-[11px] font-semibold text-slate-700 transition-colors hover:border-slate-300 hover:bg-slate-50 cursor-pointer focus:outline-none focus:ring-0';

  return (
    <div className="space-y-1.5">
      {/* Primary fields — icon chips */}
      <div className="flex items-center gap-1 flex-wrap">
        {/* Assignee */}
       
            <div>
              <Select
                open={assigneeSelectOpen}
                value={task.assignee?.id || ''}
                onValueChange={(value) => value && onAssigneeChange(value)}
                onOpenChange={setAssigneeSelectOpen}
              >
                <SelectTrigger className={cn(chipBase, 'shadow-none gap-1.5 focus:ring-0 focus:ring-offset-0')}>
                  <User className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                  <span className="truncate max-w-[100px]">
                    {task.assignee
                      ? `${task.assignee.first_name} ${task.assignee.last_name}`
                      : 'Unassigned'}
                  </span>
                </SelectTrigger>
                <SelectContent>
                  {/* Quick-assign section for CM / MP — sticky at top */}
                  {(caseManager?.display_name || managingParalegal) && (
                    <div className="sticky -top-px z-10 bg-white px-1 pt-1.5 pb-1.5 border-b border-gray-100 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                      <div className="flex flex-col gap-1">
                        {caseManager?.display_name && (
                          <button
                            type="button"
                            onClick={() => {
                              onAssigneeChange(caseManager.id);
                              setAssigneeSelectOpen(false);
                            }}
                            className={cn(
                              'flex items-center gap-1.5 w-full rounded-md px-2 py-1.5 text-xs font-semibold transition-colors text-left',
                              task.assignee?.id === caseManager.id
                                ? 'bg-blue-100 text-blue-800 ring-1 ring-blue-300'
                                : 'bg-blue-50 text-blue-700 hover:bg-blue-100',
                            )}
                          >
                            <User className="h-3 w-3 shrink-0" />
                            <span className="truncate">CM — {caseManager.display_name}</span>
                            {task.assignee?.id === caseManager.id && (
                              <Check className="ml-auto h-3 w-3 shrink-0" />
                            )}
                          </button>
                        )}
                        {managingParalegal ? (
                          <button
                            type="button"
                            onClick={() => {
                              onAssigneeChange(managingParalegal.id);
                              setAssigneeSelectOpen(false);
                            }}
                            className={cn(
                              'flex items-center gap-1.5 w-full rounded-md px-2 py-1.5 text-xs font-semibold transition-colors text-left',
                              task.assignee?.id === managingParalegal.id
                                ? 'bg-violet-100 text-violet-800 ring-1 ring-violet-300'
                                : 'bg-violet-50 text-violet-700 hover:bg-violet-100',
                            )}
                          >
                            <User className="h-3 w-3 shrink-0" />
                            <span className="truncate">MP — {managingParalegal.first_name} {managingParalegal.last_name}</span>
                            {task.assignee?.id === managingParalegal.id && (
                              <Check className="ml-auto h-3 w-3 shrink-0" />
                            )}
                          </button>
                        ) : (
                          <div className="flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium text-slate-400 cursor-not-allowed">
                            <User className="h-3 w-3 shrink-0" />
                            <span>MP — Not assigned</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {!isStaffUsersLoading &&
                    staffUsers.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.first_name} {user.last_name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
       

        {/* Next Action Due */}
        <Tooltip open={datePickerOpen ? false : undefined}>
          <TooltipTrigger asChild>
            <div className={cn(chipBase, isOverdue && 'text-red-600 border-red-200')}>
              <CalendarClock className={cn('h-3.5 w-3.5 shrink-0', isOverdue ? 'text-red-400' : 'text-slate-400')} />
              <TaskDatePicker
                value={task.due_date}
                onChange={onDueDateChange}
                onOpenChange={setDatePickerOpen}
                placeholder="Set date"
                className={cn(
                  'text-xs font-semibold',
                  isOverdue ? 'text-red-600 hover:text-red-700' : 'text-slate-700 hover:text-slate-900',
                )}
              />
            </div>
          </TooltipTrigger>
          <TooltipContent side="top"><p className="text-xs">Next Action Due</p></TooltipContent>
        </Tooltip>

        {/* Priority */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={handlePriorityClick}
              className={chipBase}
            >
              <PriorityIcon level={priority} />
              <span>{priorityLabel}</span>
            </button>
          </TooltipTrigger>
          <TooltipContent side="top"><p className="text-xs">Priority (click to cycle)</p></TooltipContent>
        </Tooltip>

   
            <button
              type="button"
              onClick={() => setShowMoreDetails((prev) => !prev)}
              className={chipBase}
            >
              <ChevronDown
                className={cn(
                  'h-3.5 w-3.5 text-slate-400 shrink-0 transition-transform',
                  showMoreDetails && 'rotate-180',
                )}
              />
              <span>{showMoreDetails ? 'Less' : 'More'}</span>
            </button>
      </div>

      {/* Expanded details */}
      {showMoreDetails && (
        <div className="flex flex-wrap gap-x-4 gap-y-1.5">
            <MetadataItem
              label="SLA Due"
              icon={<Calendar className="h-3 w-3" />}
            >
              {task.sla_resolution_target
                ? formatTaskDate(task.sla_resolution_target)
                : 'None'}
            </MetadataItem>

            {task.external_cms_update_status &&
              task.external_cms_update_status ===
                TaskExternalCMSUpdateStatus.NOT_UPDATED && (
                <MetadataItem
                  label="CMS"
                  icon={<AlertCircle className="h-3 w-3 text-amber-500" />}
                >
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCMSUpdateClick}
                    disabled={isUpdatingCMS}
                    className="h-5 px-1.5 text-[10px] font-semibold bg-amber-50 text-amber-700 hover:bg-amber-100 border-amber-200"
                  >
                    {isUpdatingCMS ? 'Updating...' : (
                      <>
                        <CheckCircle className="h-2.5 w-2.5 mr-0.5" />
                        Mark Updated
                      </>
                    )}
                  </Button>
                </MetadataItem>
              )}

            {task.external_cms_update_status ===
              TaskExternalCMSUpdateStatus.UPDATED && (
              <MetadataItem
                label="CMS"
                icon={<CheckCircle className="h-3 w-3 text-green-500" />}
              >
                <span className="text-green-700">Updated</span>
              </MetadataItem>
            )}

            <MetadataItem label="Source" icon={<Tag className="h-3 w-3" />}>
              {renderSourceContent(task)}
            </MetadataItem>

            {pushAction && (
              <MetadataItem label="Pushed to CMS">
                <CmsPushBadge action={pushAction} />
              </MetadataItem>
            )}

            {onCoverageChange && (
              <MetadataItem
                label="Coverage"
                icon={<CreditCard className="h-3 w-3" />}
              >
                {isCoveragesLoading ? (
                  <span className="text-slate-500">Loading…</span>
                ) : (
                  <Select
                    value={selectedCoverageId ?? 'unlinked'}
                    onValueChange={handleCoverageSelect}
                    disabled={isCoverageUpdating}
                  >
                    <SelectTrigger className="h-auto p-0 border-0 shadow-none hover:bg-transparent hover:text-blue-600 text-left text-[11px] font-semibold">
                      <SelectValue placeholder="Link coverage" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unlinked">No coverage linked</SelectItem>
                      {coverageOptions.length === 0 ? (
                        <SelectItem value="__disabled" disabled>
                          No coverages available
                        </SelectItem>
                      ) : (
                        coverageOptions.map((coverage) => (
                          <SelectItem key={coverage.id} value={coverage.id}>
                            {formatCoverageLabel(coverage)}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                )}
              </MetadataItem>
            )}

            {task.completed_at && (
              <MetadataItem
                label="Completed"
                icon={<Calendar className="h-3 w-3" />}
              >
                <DatePicker
                  date={task.completed_at}
                  onChange={onCompletedDateChange}
                  placeholder="Set completed date"
                  className="text-[11px] font-semibold"
                />
              </MetadataItem>
            )}
          </div>
        )}
    </div>
  );
};
